import { useQuery, useQueryClient } from "@tanstack/vue-query";
import { computed, unref, type MaybeRef } from "vue";

import { ok } from "@/lib/openapi";
import { findDataResults, findStaticResults } from "@/search/executor";
import {
  useNewsletter,
  useNewsletterPermissions,
} from "@/store/stores/account";
import type { SearchResult, SearchResultWithId } from "@/types/search_result";

import internalApi from "../../internal-base";
import { useNewsletterId } from "../utils";

const MAX_BRIEF_ITEMS = 15;

const normalizeQuery = (q: string) => {
  return (
    q
      // trim leading, trailing and repetitive whitespace
      .replace(/^ +|[ .,@]+$| +(?= )/g, "")
      // lowercase everything
      .toLowerCase()
  );
};

export const useSearchQuery = ({
  q: qRef,
  scopes: scopesRef = [],
  brief = false,
  // We need this mostly to make sure we don't cache contextual routes
  // that bleed into other, irrelevant pages.
  pathname,
}: {
  q: MaybeRef<string>;
  scopes?: MaybeRef<string[]>;
  brief?: boolean;
  pathname?: string;
}) => {
  const newsletterId = useNewsletterId();
  const queryClient = useQueryClient();

  const newsletter = useNewsletter();
  const permissions = useNewsletterPermissions();

  const normalizedQuery = computed(() => normalizeQuery(unref(qRef)));

  const query = useQuery({
    queryKey: [
      newsletterId,
      "search",
      { q: normalizedQuery, scopes: scopesRef, brief, pathname },
    ],
    enabled: () => normalizedQuery.value.length !== 0,
    async queryFn({ signal }): Promise<SearchResultWithId[]> {
      const scopes = unref(scopesRef);

      const data = await ok(
        internalApi.post("/search", {
          signal,
          body: {
            text: normalizedQuery.value,
            scopes: scopes.length !== 0 ? scopes : undefined,
          },
        })
      );

      let onlineResults = data.results as unknown as SearchResult[];
      if (brief) {
        onlineResults = onlineResults.slice(0, MAX_BRIEF_ITEMS);
      }

      // Merge online data results with static results (pages, integrations)
      const staticResults = findStaticResults({
        q: normalizedQuery.value,
        permissions: permissions.value,
        scopes: scopes,
      });

      const allResults = [...onlineResults, ...staticResults];

      return allResults.map((result) => ({
        id: `${result.type}:${result.obj.id}`,
        ...result,
      }));
    },
    initialDataUpdatedAt: 0,
    initialData(): SearchResultWithId[] {
      const query = normalizedQuery.value;
      if (query === "") {
        return [];
      }

      const scopes = unref(scopesRef);

      let dataResults = findDataResults({
        q: query,
        newsletter: newsletter.value,
        queryClient: queryClient,
        scopes: scopes,
      });
      if (brief) {
        dataResults = dataResults.slice(0, MAX_BRIEF_ITEMS);
      }

      const staticResults = findStaticResults({
        q: query,
        permissions: permissions.value,
        scopes: scopes,
      });

      const results = [...dataResults, ...staticResults];

      return results.map((result) => ({
        id: `${result.type}:${result.obj.id}`,
        ...result,
      }));
    },
  });

  return query;
};
